import type { CartOutputDto } from "@sticker-giant/sg-frontend-common";
import { type UseMutationResult, useMutation, useQueryClient } from "@tanstack/react-query";
import { trackItemAddedToCart } from "@/lib/analytics/segment";
import type { ReorderIntent } from "@/lib/reorder/oneClickReorderParams";
import { commitCart } from "@/queries/useCart";
import { useGlobalStore } from "@/stores/useGlobalStore";
import { useServicesStore } from "@/stores/useServicesStore";

export const oneClickReorderMutationKey = ["oneClickReorder"] as const;

/**
 * One-click reorder: the service clones an item from a past order into a fresh cart.
 * The endpoint is authenticated and enforces item ownership server-side, so a leaked
 * or forwarded link is refused there rather than screened here.
 */
export function useOneClickReorder(): UseMutationResult<CartOutputDto, Error, ReorderIntent> {
  const cartClient = useServicesStore(state => state.cartClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: oneClickReorderMutationKey,
    mutationFn: async ({ originalItemId, couponCode }: ReorderIntent): Promise<CartOutputDto> => {
      if (!cartClient) throw new Error("Cart client is not available.");

      const response = await cartClient.postOneClickReorder(originalItemId.split(","), couponCode);
      return response.cart;
    },
    onSuccess: cart => {
      commitCart(queryClient, cart);
      useGlobalStore.getState().setCartId(cart.id);
      for (const cartItem of cart.items) {
        trackItemAddedToCart({ cartItemResponse: { cart, cartItem }, isEditMode: false, buyNow: true });
      }
    },
  });
}
